import { ApolloError } from '@apollo/client/errors';

/**
 * Serializable error type for Redux state.
 * Redux requires all state to be serializable, so we cannot store
 * Error or ApolloError objects directly as they contain non-serializable
 * values like functions, circular references, etc.
 */
export interface SerializableError {
    message: string;
    name?: string;
    stack?: string;
    graphQLErrors?: Array<{ message: string; extensions?: any }>;
    networkError?: { message: string; statusCode?: number };
}

/**
 * Converts an Error or ApolloError into a serializable format
 * that can be safely stored in Redux state.
 * 
 * @param error - The error to serialize (Error, ApolloError, or any)
 * @returns A plain object containing only serializable error information
 */
export const serializeError = (error: Error | ApolloError | any): SerializableError => {
    if (!error) {
        return { message: 'Unknown error' };
    }
    
    const serialized: SerializableError = {
        message: error.message || 'Unknown error',
        name: error.name,
        stack: error.stack,
    };

    // Handle ApolloError specifics
    if ('graphQLErrors' in error && Array.isArray(error.graphQLErrors)) {
        serialized.graphQLErrors = error.graphQLErrors.map((err: any) => ({
            message: err.message,
            extensions: err.extensions,
        }));
    }

    if ('networkError' in error && error.networkError) {
        const networkError = error.networkError;
        serialized.networkError = {
            message: networkError.message || 'Network error',
            statusCode: (networkError as any).statusCode,
        };
    }

    return serialized;
};
